// worldGlobe — dotted world-map globe (continents rendered as dots on a slowly
// rotating sphere, with glowing location pins). Brand-violet on dark; matches
// the "tecnología para escalar" reference. Self-contained, no map asset: land is
// derived from coarse continent polygons via point-in-polygon.
//
// a11y / perf: prefers-reduced-motion → static frame; pauses off-screen.

// Coarse continent outlines as [lon, lat] vertex loops (enough to read as Earth).
const CONTINENTS = [
  // North America
  [[-168,65],[-160,71],[-128,70],[-95,70],[-82,73],[-62,60],[-56,52],[-66,46],[-70,42],[-75,35],[-81,25],[-97,18],[-106,23],[-112,30],[-122,34],[-125,40],[-130,52],[-140,60],[-168,65]],
  // Greenland
  [[-55,60],[-30,60],[-18,70],[-22,80],[-45,83],[-58,72],[-55,60]],
  // South America
  [[-80,9],[-60,11],[-50,0],[-35,-6],[-40,-22],[-50,-30],[-58,-40],[-66,-52],[-73,-50],[-71,-30],[-74,-16],[-81,-4],[-80,9]],
  // Africa
  [[-16,16],[-9,32],[10,37],[33,32],[44,12],[51,12],[42,-2],[40,-18],[33,-29],[20,-35],[14,-28],[12,-6],[8,5],[-8,5],[-16,16]],
  // Europe
  [[-10,37],[-2,44],[2,51],[8,58],[12,66],[28,71],[42,67],[60,66],[55,52],[40,46],[28,41],[20,38],[6,38],[-10,37]],
  // Asia
  [[40,46],[55,52],[60,66],[90,76],[140,73],[162,68],[172,66],[150,60],[145,46],[140,35],[123,40],[122,28],[108,20],[95,8],[80,7],[72,22],[60,26],[52,40],[40,46]],
  // India peninsula
  [[68,24],[78,30],[88,24],[88,16],[80,7],[73,16],[68,24]],
  // SE Asia / Indonesia (rough)
  [[95,6],[105,2],[120,0],[132,-3],[140,-8],[120,-9],[105,-6],[98,2],[95,6]],
  // Australia
  [[113,-22],[130,-12],[142,-12],[150,-25],[153,-35],[140,-38],[128,-32],[115,-35],[113,-22]],
]

// Glowing pins (lon, lat) — a few hubs across the Americas / EU.
const PINS = [
  [-77, -12], // Lima
  [-74, 40],  // NYC
  [-99, 19],  // CDMX
  [-46, -23], // São Paulo
  [-3, 40],   // Madrid
  [114, -8],  // SE Asia
]

const DEG = Math.PI / 180
const TILT = -0.35 // axial tilt for a pleasant angle
const SPEED = 0.0022

function pointInPoly(lon, lat, poly) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1]
    const xj = poly[j][0], yj = poly[j][1]
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function isLand(lon, lat) {
  for (let k = 0; k < CONTINENTS.length; k++) if (pointInPoly(lon, lat, CONTINENTS[k])) return true
  return false
}

function unit(lon, lat) {
  const la = lat * DEG, lo = lon * DEG
  return [Math.cos(la) * Math.cos(lo), Math.sin(la), Math.cos(la) * Math.sin(lo)]
}

function buildDots() {
  const dots = []
  for (let lat = -56; lat <= 83; lat += 3) {
    for (let lon = -180; lon < 180; lon += 3) {
      if (isLand(lon, lat)) dots.push(unit(lon, lat))
    }
  }
  return dots
}

function initWorldGlobe(canvas) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const dots = buildDots()
  const pins = PINS.map((p) => unit(p[0], p[1]))

  let w = 0, h = 0, R = 0, cx = 0, cy = 0, yaw = 0, raf = 0, running = false, t = 0

  function resize() {
    const rect = canvas.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    w = rect.width; h = rect.height
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    cx = w / 2; cy = h / 2
    R = Math.min(w, h) * 0.49
  }

  const cosT = Math.cos(TILT), sinT = Math.sin(TILT)
  function project(p) {
    const cy_ = Math.cos(yaw), sy = Math.sin(yaw)
    const x1 = p[0] * cy_ + p[2] * sy
    const z1 = -p[0] * sy + p[2] * cy_
    const y2 = p[1] * cosT - z1 * sinT
    const z2 = p[1] * sinT + z1 * cosT
    return { x: cx + x1 * R, y: cy - y2 * R, z: z2 }
  }

  function draw() {
    ctx.clearRect(0, 0, w, h)

    // Soft sphere glow + rim.
    const g = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R)
    g.addColorStop(0, 'rgba(133,69,234,0.16)')
    g.addColorStop(1, 'rgba(133,69,234,0)')
    ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill()
    ctx.strokeStyle = 'rgba(168,123,236,0.18)'; ctx.lineWidth = 1
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke()

    // Land dots (front hemisphere; depth-shaded).
    for (let i = 0; i < dots.length; i++) {
      const q = project(dots[i])
      if (q.z < -0.02) continue
      const front = Math.max(0, q.z)
      ctx.fillStyle = `rgba(${190 - front * 0}, ${150 + front * 40}, ${245},${0.22 + front * 0.6})`
      ctx.beginPath(); ctx.arc(q.x, q.y, 1.15 + front * 0.9, 0, Math.PI * 2); ctx.fill()
    }

    // Pins with pulsing halo.
    const pulse = (Math.sin(t * 2.2) + 1) / 2
    for (let i = 0; i < pins.length; i++) {
      const q = project(pins[i])
      if (q.z < 0.08) continue
      const ph = (Math.sin(t * 2.2 + i) + 1) / 2
      ctx.fillStyle = `rgba(199,180,245,${0.12 + ph * 0.18})`
      ctx.beginPath(); ctx.arc(q.x, q.y, 6 + ph * 7, 0, Math.PI * 2); ctx.fill()
      ctx.shadowColor = 'rgba(133,69,234,0.9)'; ctx.shadowBlur = 10
      ctx.fillStyle = '#EBE2FF'
      ctx.beginPath(); ctx.arc(q.x, q.y, 2.6, 0, Math.PI * 2); ctx.fill()
      ctx.shadowBlur = 0
    }
  }

  function frame() { yaw += SPEED; t += 0.016; draw(); if (running) raf = requestAnimationFrame(frame) }
  function start() { if (running || reduce) return; running = true; raf = requestAnimationFrame(frame) }
  function stop() { running = false; if (raf) cancelAnimationFrame(raf) }

  resize(); draw()
  let rt = 0
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { resize(); draw() }, 150) }, { passive: true })
  if (reduce) return
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? start() : stop())), { threshold: 0.04 }).observe(canvas)
  } else start()
}

function initWorldGlobes() { document.querySelectorAll('[data-world-globe]').forEach(initWorldGlobe) }
if (document.readyState !== 'loading') initWorldGlobes()
else document.addEventListener('DOMContentLoaded', initWorldGlobes)
